import React, { useEffect, useState } from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import Footer from "../../components/footer/Footer";
import styles from "../../styles/__login.module.scss";
import CountryCodeSelect from "../../components/country-code-select/CountryCodeSelect";
import {
  DEFAULT_COUNTRY_ISO,
  findCountryByIso,
} from "../../constants/countries";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { postLogin } from "../../reducer/login/loginReducer";
import {
  getMobileLengthIssue,
  limitMobileDigits,
  mobileLengthErrorText,
} from "../../utils/phoneValidation";

const LoginScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Report the selected role up to the layout so the Header can hide the language dropdown
  // for the Pharmacy role (shown for Doctor only).
  const { setLoginRole } = useOutletContext() || {};
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState("Doctor");
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [mobileNumber, setMobileNumber] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  // Set by the first sign-in attempt; from then on a too-short number is flagged as it changes.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  // Same country list (with search and flags) as every other mobile number field in the app.
  const selectedCountryData = findCountryByIso(countryIso);

  const languages = [
    { code: "en", name: "English", flag: "Flag_of_United_States-64x34.png" },
    { code: "ar", name: "العربية", flag: "Flag_of_Iraq-64x43.png" },
  ];

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = lang === "ar" ? "rtl" : "ltr";
  };

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language.name);
    i18n.changeLanguage(language.code);
    changeLanguage(language.code);
    setIsLanguageDropdownOpen(false);
  };

  // Set document direction on mount based on current language
  useEffect(() => {
    document.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  // Sync selectedLanguage with i18n.language on mount and when language changes
  useEffect(() => {
    const currentLang = languages.find((lang) => lang.code === i18n.language);
    if (currentLang) {
      setSelectedLanguage(currentLang.name);
    }
  }, [i18n.language]);

  // Digit count required for the selected country (libphonenumber-js metadata). Recomputed on
  // every render, so it follows a change of country code immediately.
  const mobileLengthIssue = getMobileLengthIssue(
    mobileNumber,
    selectedCountryData?.iso,
  );
  const mobileLengthError = mobileLengthIssue
    ? mobileLengthErrorText(t, i18n.language, selectedCountryData, mobileLengthIssue)
    : "";

  // Shown under the mobile number: "required" from the last sign-in attempt while empty;
  // otherwise the length error (too long at once, e.g. after switching country; too short
  // once a sign-in has been attempted).
  const mobileError = mobileLengthIssue
    ? submitAttempted || mobileLengthIssue.tooLong
      ? mobileLengthError
      : ""
    : errors?.mobileNumber;

  // A stored error belongs to the number/country it was raised for.
  const clearMobileError = () =>
    setErrors((prev) =>
      prev?.mobileNumber ? { ...prev, mobileNumber: undefined } : prev,
    );

  const validateForm = () => {
    const newErrors = {};

    // Validation based on role
    if (selectedRole === "Doctor") {
      // Mobile validation for Doctor
      if (!mobileNumber.trim()) {
        newErrors.mobileNumber = t("login.validation.mobileRequired");
      } else if (mobileLengthIssue) {
        newErrors.mobileNumber = mobileLengthError;
      } else if (!/^\d{7,15}$/.test(mobileNumber)) {
        newErrors.mobileNumber = t("login.validation.mobileInvalid");
      }
    } else if (selectedRole === "Pharmacy") {
      // Username validation for Pharmacy
      if (!username.trim()) {
        newErrors.username = t("login.validation.usernameRequired");
      } else if (username.length < 3) {
        newErrors.username = t("login.validation.usernameTooShort");
      }
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = t("login.validation.passwordRequired");
    } else if (password.length < 8) {
      newErrors.password = t("login.validation.passwordMinLength");
    } else if (!/[a-z]/.test(password)) {
      newErrors.password = t("login.validation.passwordLowercase");
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = t("login.validation.passwordUppercase");
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = t("login.validation.passwordNumber");
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      newErrors.password = t("login.validation.passwordSpecial");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const passwordRules = {
    length: { test: (pwd) => pwd.length >= 8, message: t("charcters-8") },
    lowercase: { test: (pwd) => /[a-z]/.test(pwd), message: t("lowerCase") },
    uppercase: { test: (pwd) => /[A-Z]/.test(pwd), message: t("upperCase") },
    number: { test: (pwd) => /[0-9]/.test(pwd), message: t("oneNumber") },
    special: {
      test: (pwd) => /[!@#$%^&*(),.?\":{}|<>]/.test(pwd),
      message: t("specialCharacter"),
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      console.log("Login attempt:", {
        selectedRole,
        countryCode: selectedCountryData.code,
        mobileNumber,
        username,
        password,
      });
      const roleId =
        selectedRole === "Doctor" ? 4 : selectedRole === "Pharmacy" ? 5 : 4;
      const loginData = {
        role_id: roleId,
        ...(selectedRole === "Doctor"
          ? { mobile_number: `${selectedCountryData.code}${mobileNumber}` }
          : { username: username }),
        password: password,
      };

      const result = await dispatch(postLogin(loginData));
      if (result && result?.payload) {
        // Save tokens
        sessionStorage.setItem(
          "usr_1a2b3c",
          result?.payload?.data?.access_token,
        );
        sessionStorage.setItem(
          "usr_4d5e6f",
          result?.payload?.data?.refresh_token,
        );
        // Store role_id for route protection
        if (result?.payload?.data?.user?.role_id) {
          sessionStorage.setItem(
            "usr_role_id",
            result?.payload?.data?.user?.role_id.toString(),
          );
        }
        // Store verification status for access control
        if (result?.payload?.data?.verification_status) {
          sessionStorage.setItem(
            "usr_verification_status",
            result?.payload?.data?.verification_status,
          );
        }
        if (result?.payload?.data?.is_uploaded !== undefined) {
          sessionStorage.setItem(
            "usr_is_uploaded",
            result?.payload?.data?.is_uploaded.toString(),
          );
        }
        if (result?.payload?.data?.is_registration_complete !== undefined) {
          sessionStorage.setItem(
            "usr_is_registration_complete",
            result?.payload?.data?.is_registration_complete.toString(),
          );
        }
        // Store consultation_type for address dropdown visibility
        if (result?.payload?.data?.user?.consultation_type) {
          sessionStorage.setItem(
            "usr_consultation_type",
            result?.payload?.data?.user?.consultation_type,
          );
        }

        // Navigate after login based on verification status and registration completion
        const isUploaded = result?.payload?.data?.is_uploaded;
        const isRegistrationComplete =
          result?.payload?.data?.is_registration_complete;
        const verificationStatus = result?.payload?.data?.verification_status;
        const roleId = result?.payload?.data?.user?.role_id;

        // Doctor role navigation logic
        // Flow: Rules → Registration → Upload Documents → Documents Under Verification → Dashboard
        if (roleId === 4) {
          // Case 1: Registration complete, documents uploaded, APPROVED -> Dashboard
          if (
            isRegistrationComplete === true &&
            isUploaded === true &&
            verificationStatus === "APPROVED"
          ) {
            navigate("/dashboard");
          }
          // Case 2: Registration complete, documents uploaded, PENDING -> Documents Under Verification
          else if (
            isRegistrationComplete === true &&
            isUploaded === true &&
            verificationStatus === "PENDING"
          ) {
            navigate("/documents-under-process");
          }
          // Case 3: Registration complete, documents NOT uploaded -> Upload Documents
          else if (isRegistrationComplete === true && isUploaded === false) {
            navigate("/upload-required-documents");
          }
          // Case 4: Registration NOT complete -> Rules & Registration
          else if (isRegistrationComplete === false) {
            navigate("/doctor-rules");
          }
          // Case 5: Status REJECTED -> Upload Documents (re-upload required)
          else if (verificationStatus === "REJECTED") {
            navigate("/upload-required-documents");
          }
          // Default fallback for doctor
          else {
            navigate("/dashboard");
          }
        }
        // Pharmacy role navigation logic
        else if (roleId === 5) {
          console.log("Pharmacy login, navigating to pharmacy dashboard");
          navigate("/pharmacy-dashboard");
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles["main-container"]}>
      {/* Main Content */}
      <main className={styles["main-content"]}>
        <div className={styles["content-wrapper"]}>
          {/* Doctor Illustration */}
          <div className={styles["illustration"]}>
            <div className={styles["doctor-illustration"]}>
              <img
                src="/login-img-1.png"
                alt={t("login.illustrationAlt")}
                className={styles["doctor-image"]}
              />
            </div>
          </div>

          {/* Login Form */}
          <div className={styles["login-form-container"]}>
            <h2>{t("loginToAccount")}</h2>
            <form onSubmit={handleSubmit} className={styles["login-form"]}>
              {/* Role Selection */}
              <div className={styles["form-group"]}>
                <label htmlFor={styles["role"]}>{t("selectRole")}</label>
                <div className={styles["select-wrapper"]}>
                  <select
                    id="role"
                    value={selectedRole}
                    onChange={(e) => {
                      const role = e.target.value;
                      setSelectedRole(role);
                      if (setLoginRole) setLoginRole(role);
                      // Pharmacy is English-only: force English (and LTR) when switching to it.
                      if (role === "Pharmacy") {
                        changeLanguage("en");
                        setSelectedLanguage("English");
                      }
                    }}
                    className={styles["form-select"]}
                  >
                    <option value="Doctor">{t("doctor")}</option>
                    <option value="Pharmacy">{t("pharmacy")}</option>
                  </select>
                  <ChevronDown className={styles["select-icon"]} size={20} />
                </div>
              </div>

              {/* Mobile Number for Doctor / Username for Pharmacy */}
              {selectedRole === "Doctor" ? (
                <div className={styles["form-group"]}>
                  <label htmlFor="mobile">
                    {t("mobileNumber")} <span style={{ color: "red" }}>*</span>
                  </label>
                  <div className={styles["mobile-input-container"]}>
                    <CountryCodeSelect
                      className={styles["country-selector"]}
                      value={countryIso}
                      onChange={(country) => {
                        setCountryIso(country.iso);
                        clearMobileError();
                      }}
                    />
                    <input
                      type="tel"
                      id="mobile"
                      placeholder="770 123 4567"
                      inputMode="numeric"
                      value={mobileNumber}
                      onChange={(e) => {
                        // Digits only (Arabic-keyboard digits become 0-9), no leading 0
                        // (the country code replaces the trunk 0), and no more digits than
                        // the selected country's mobile numbers have.
                        setMobileNumber(
                          limitMobileDigits(e.target.value, selectedCountryData?.iso),
                        );
                        clearMobileError();
                      }}
                      aria-invalid={!!mobileError || undefined}
                      aria-describedby={mobileError ? "mobile-error" : undefined}
                      className={styles["phone-input"]}
                    />
                  </div>
                  {mobileError && (
                    <p
                      id="mobile-error"
                      role="alert"
                      className={styles["rule-invalid"]}
                    >
                      {mobileError}
                    </p>
                  )}
                </div>
              ) : (
                <div className={styles["form-group"]}>
                  <label htmlFor="username">
                    {t("username")} <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    placeholder={t("usernamePlaceholder")}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={styles["form-input"]}
                  />
                  {errors?.username && (
                    <p className={styles["rule-invalid"]}>{errors?.username}</p>
                  )}
                </div>
              )}
              {/* Password */}
              <div className={styles["form-group"]}>
                <label htmlFor="password">
                  {t("password")} <span style={{ color: "red" }}>*</span>
                </label>
                <div className={styles["password-wrapper"]}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={styles["form-input"]}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles["password-toggle"]}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <div className={styles["password-rules"]}>
                  {Object.keys(passwordRules).map((ruleKey) => {
                    const rule = passwordRules[ruleKey];
                    const isValid = rule.test(password);
                    return (
                      <p
                        key={ruleKey}
                        className={
                          isValid
                            ? styles["rule-valid"]
                            : styles["rule-invalid"]
                        }
                      >
                        {isValid ? "✔" : "✖"} {rule.message}
                      </p>
                    );
                  })}
                </div>
                <p className={styles["Forgot-pwd-link"]}>
                  <Link to="/forgot-password">{t("forgotPassword")}?</Link>
                </p>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                className={styles["login-btn"]}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span
                    className={styles["login-btn-loader"]}
                    aria-hidden="true"
                  ></span>
                ) : (
                  t("login")
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LoginScreen;
